import { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import API from '../config/axiosConfig';
import LoadingSubmitButton from '../components/LoadingSubmitButton';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import md5 from 'md5';
import { isEntraSigninAvailable, isLegacyLoginClosed } from '../auth/capabilityPolicy';
import CutoffLogin from './CutoffLogin';
import { hasMigrated } from '../auth/migratedFlag';

// Replaces the old browser-wide `isFirstLogin` flag, which meant the second person to
// sign in on a shared machine never saw /welcome. Keyed per user instead.
const WELCOME_SEEN_PREFIX = 'mashlom.welcomeSeen.';
const LEGACY_WELCOME_KEY = 'isFirstLogin';

// The server's own wording when sign-in attempts are rate-limited (429), so a
// locked-out user is not told to recheck a password that may be right.
const rateLimitMessage = (error) => (
  error?.response?.status === 429 ? error.response.data?.error?.hebMessage || null : null
);

// True the first time this user signs in on this browser. Whoever signs in first after
// the upgrade inherits the legacy flag, so existing users aren't shown /welcome again.
const shouldShowWelcome = (token) => {
  try {
    const userId = jwtDecode(token)?.id;
    if (!userId) return false;
    const key = `${WELCOME_SEEN_PREFIX}${userId}`;
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, 'true');
    if (localStorage.getItem(LEGACY_WELCOME_KEY)) {
      localStorage.removeItem(LEGACY_WELCOME_KEY);
      return false;
    }
    return true;
  } catch (err) {
    // Unreadable token, or storage unavailable (private window, blocked site data).
    // Skip rather than show /welcome on every single login.
    console.error('Reading welcome-seen flag failed:', err);
    return false;
  }
};

// The password / Google login. Microsoft sign-in has its own entry point,
// /signin (pages/Signin.jsx), which goes straight to Microsoft's page.
function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [focusedInput, setFocusedInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    login,
    authCapabilities,
    authError: contextAuthError,
  } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const googleLoginButtonRef = useRef(null);

  useEffect(() => {
    if (contextAuthError) setError(contextAuthError);
  }, [contextAuthError]);

  // A Microsoft redirect can place this page in the browser's back-forward
  // cache before the pending promise reaches its finally block. Clear transient
  // submit state whenever the browser restores the page.
  useEffect(() => {
    const resetTransientState = () => setIsSubmitting(false);
    window.addEventListener('pageshow', resetTransientState);
    return () => window.removeEventListener('pageshow', resetTransientState);
  }, []);

  // MD5 hash of "emek-token" for verification
  const EXPECTED_TOKEN_HASH = '61ec1d72725a0ade55c30b3a623058e2';

  const finishWithToken = async (token, extras = {}) => {
    await login(token);

    const from = location.state?.from?.pathname + location.state?.from?.search + location.state?.from?.hash;
    const isFromLogin = location.state?.from?.pathname === '/login';
    if (extras.isSignup) {
      const queryParams = new URLSearchParams({
        step: 2,
        name: extras.name,
        email: extras.email,
        isGoogle: true,
        userId: extras.userId,
      }).toString();
      navigate(`/onboarding/register?${queryParams}`, { replace: true });
    } else if (from && !isFromLogin) {
      navigate(from, { replace: true });
    } else if (shouldShowWelcome(token)) {
      navigate('/welcome', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleLogin = async (url, body) => {
    setIsSubmitting(true);
    setError('');
    try {
      const response = await API.post(url, body, {
        handlingErrorLocally: true,
      });
      const { token, isSignup, name, email: signupEmail, userId } = response.data;

      if (!token) {
        setError('התחברות נכשלה. אנא בדוק את הפרטים ונסה שוב.');
        return;
      }

      await finishWithToken(token, { isSignup, name, email: signupEmail, userId });
    } catch (error) {
      console.error('Login failed:', error);
      setError(rateLimitMessage(error) || 'התחברות נכשלה. אנא בדוק את הפרטים ונסה שוב.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const username = params.get('username');
    const password = params.get('password');
    const uiToken = params.get('uiToken');

    // Check if all required parameters are present
    if (username && password && uiToken) {
      // Prevent super admin from using automatic login
      if (username === 'admin@mashlom.me') {
        setError('אירעה שגיאת אבטחת מידע חמורה. האירוע דווח אוטומטית למשטרת ישראל.');
        return;
      }

      // Verify the token by hashing it and comparing with expected hash
      const tokenHash = md5(uiToken);

      if (tokenHash === EXPECTED_TOKEN_HASH) {
        handleLogin('/api/auth/login', { email: username, password });
      } else {
        setError('הקישור אינו תקין. אנא פנה למנהל המערכת.');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('כתובת האימייל אינה תקינה.');
      return;
    }

    handleLogin('/api/auth/login', { email, password });
  };

  const handleGoogleSuccess = (response) => {
    const googleToken = response.credential;
    handleLogin('/api/auth/google', { googleToken });
  };

  const handleGoogleError = (err) => {
    console.error('Google login failed:', err);
    setError('התחברות עם Google נכשלה. נסה שוב.');
  };

  const getLabelStyle = (inputName) => ({
    right: '12px',
    top: '0',
    transition: 'all 0.3s ease',
    transform:
      focusedInput === inputName || (inputName === 'email' ? email : password)
        ? 'translateY(4px) scale(0.75)'
        : 'translateY(12px)',
    transformOrigin: 'right top',
  });

  // From the cutoff /login is the new page (docs/EntraFullMigrationPlan.md §3.2).
  // `?preview=cutoff` shows it before the cutoff, to try it on production: only
  // in the browser that opens that link, and the server closes nothing.
  const previewCutoff = isEntraSigninAvailable(authCapabilities)
    && new URLSearchParams(location.search).get('preview') === 'cutoff';
  if (isLegacyLoginClosed(authCapabilities) || previewCutoff) {
    // A browser that has signed in with Microsoft before goes straight to
    // Microsoft's sign-in page, with the page to return to. Not after a failed
    // sign-in (the error screens link here with `?explain`), or it would loop.
    const explain = new URLSearchParams(location.search).has('explain');
    if (hasMigrated() && !contextAuthError && !explain) {
      return <Navigate replace to="/signin" state={location.state} />;
    }
    return <CutoffLogin />;
  }

  const inputStyle = {
    borderWidth: '2px',
    borderRadius: '8px',
  };

  return (
    <div
      className="d-flex justify-content-center align-items-center"
      dir="rtl"
      style={{ backgroundColor: 'white', height: '100%' }}
    >
      <form
        noValidate
        onSubmit={handleSubmit}
        style={{
          width: '80dvw',
          maxWidth: '350px',
          padding: '20px',
        }}
      >
        <div className="form-group mb-3 position-relative">
          <input
            type="email"
            id="inputEmail"
            className="form-control pt-4"
            required
            autoFocus
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError('');
            }}
            onFocus={() => setFocusedInput('email')}
            onBlur={() => setFocusedInput('')}
            style={inputStyle}
          />
          <label
            htmlFor="inputEmail"
            className="position-absolute text-muted"
            style={getLabelStyle('email')}
          >
            כתובת אימייל
          </label>
        </div>
        <div className="form-group mb-3 position-relative">
          <input
            type="password"
            id="inputPassword"
            className="form-control pt-4"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setFocusedInput('password')}
            onBlur={() => setFocusedInput('')}
            style={inputStyle}
          />
          <label
            htmlFor="inputPassword"
            className="position-absolute text-muted"
            style={getLabelStyle('password')}
          >
            סיסמה
          </label>
        </div>
        <div className="text-center">
          <LoadingSubmitButton
            isLoading={isSubmitting}
            text="התחבר"
            loadingText="מתחבר..."
            style={{
              backgroundColor: '#103C6E',
              color: 'white',
              width: '100%',
              height: '48px',
              fontSize: '1.25rem',
              fontWeight: '500',
            }}
            className="btn-lg"
          />
        </div>
        {error && (
          <div
            style={{
              color: 'red',
              marginTop: '20px',
              textAlign: 'center',
              fontSize: '0.9em',
            }}
          >
            {error}
          </div>
        )}
        <div className="mt-3">
          <button
            type="button"
            onClick={() => {
              // a hack to trigger the google login button
              const googleButton = document.querySelector(
                'div[role="button"]',
              );
              if (googleButton) {
                googleButton.click();
              }
            }}
            className="flex items-center justify-center w-full h-12 px-4 text-base font-medium text-center text-[#103C6E] border-[#103C6E] hover:bg-gray-100 border-1"
            style={{borderRadius: '8px'}}
          >
            התחברות באמצעות Google
          </button>
          <div style={{ display: 'none' }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="outline"
              width="100%"
              text="continue_with"
              locale="he"
              ref={googleLoginButtonRef}
            />
          </div>
        </div>
        <div className="text-center mt-4">
          <Link
              to="/reset-password"
              className="underline"
              style={{ color: '#2D598B' }}
            >
              שכחתי סיסמה
          </Link>
        </div>
        {/* Before the cutoff new users take the legacy onboarding; from it this
            page is CutoffLogin. */}
        <div className="text-center mt-4">
          <Link to="/onboarding" className="underline" style={{ color: '#2D598B' }}>
            עוד לא נרשמת? ברוכים הבאים
          </Link>
        </div>
      </form>
    </div>
  );
}

export default Login;
